import {useCallback} from 'react';

import {dateRangeToWorksheetName} from '../../../../../../component/request-report-base/request-report-base-helper';
import {PageCard} from '../../../../../../layout/page-card/page-card';
import {Locale} from '../../../../../../provider/locale/locale';
import {useLocale} from '../../../../../../provider/locale/locale-hook';
import {getUserTags} from '../../../../../../service/reivew-analysis/review-analytics-tags/review-analytics-tags-api';
import {
    ReviewsAnalysisTagsQueryType,
    ReviewsAnalysisUserTagType,
    ReviwsAnalysisUserTagTableDataType,
} from '../../../../../../service/reivew-analysis/review-analytics-tags/review-analytics-tags-type';
import {ReviewsAndAnswersStatisticsUnitMeasurementEnum} from '../../../../../../service/reviews-and-answers-statistics/reviews-and-answers-statistics';
import {TimeSizeEnum} from '../../../../../../util/format';
import {useFormat} from '../../../../../../util/format-hook/format-hook';
import {ActiveFilter} from '../../../components/active-filter/active-filter';
import {RequestReport} from '../../../components/request-report/request-report';
import {useReviewsAnalysisFilter} from '../../../reviews-analysis-hook';
import {CompareModeResolver} from '../../dashboard/compare-mode-resolver/compare-mode-resolver';
import {userTagsPageTableColumns} from '../keywords-and-topics-page-helper';

import {UserTagsTablePeriod} from './user-tags-period/user-tags-period';
import {UserTagsTableSingle} from './user-tags-single/user-tags-single';
import * as styles from './user-tags-tab.scss';

export function UserTagsTab(): JSX.Element {
    const {singleFilter} = useReviewsAnalysisFilter();

    const {getLocalizedString} = useLocale();

    const {getFormattedNumber} = useFormat();

    const getDataSource = useCallback((array: Array<ReviewsAnalysisUserTagType>) => {
        return array.map<ReviwsAnalysisUserTagTableDataType>(
            (tagItem: ReviewsAnalysisUserTagType, index): ReviwsAnalysisUserTagTableDataType => ({
                key: `${tagItem.title} + ${index}`,
                ...tagItem,
            })
        );
    }, []);

    function formatDataForXls(data: Array<ReviewsAnalysisUserTagType>) {
        return data.map((tag) => ({
            [getLocalizedString('REVIEWS_ANALYSIS__TABLE__COLUMN__TAG')]: tag.title,
            [getLocalizedString('REVIEWS_ANALYSIS__TABLE__COLUMN__RATE')]: tag.avgRating.toFixed(1),
            [getLocalizedString('REVIEWS_ANALYSIS__TABLE__COLUMN__REVIEW_TOTAL')]: tag.total,
            [getLocalizedString('REVIEWS_ANALYSIS__TABLE__COLUMN__REVIEWS_REPLIED')]: tag.replied,
            [getLocalizedString('REVIEWS_ANALYSIS__TABLE__COLUMN__REPLY_DELAY')]: tag.avgResponseSpeed
                ? getFormattedNumber(tag.avgResponseSpeed, {style: 'unit', unit: TimeSizeEnum.minute})
                : '-',
            [`${getLocalizedString('REVIEWS_ANALYSIS__TABLE__COLUMN__SENTIMENT')}: ${getLocalizedString(
                'REVIEWS_ANALYSIS__SENTIMENT__POSITIVE'
            )}`]: tag.sentiment.positive,
            [`${getLocalizedString('REVIEWS_ANALYSIS__TABLE__COLUMN__SENTIMENT')}: ${getLocalizedString(
                'REVIEWS_ANALYSIS__SENTIMENT__NEUTRAL'
            )}`]: tag.sentiment.neutral,
            [`${getLocalizedString('REVIEWS_ANALYSIS__TABLE__COLUMN__SENTIMENT')}: ${getLocalizedString(
                'REVIEWS_ANALYSIS__SENTIMENT__NEGATIVE'
            )}`]: tag.sentiment.negative,
        }));
    }

    return (
        <PageCard
            title={
                <div className={styles.UserTagsTab}>
                    <div className={styles.UserTagsTab_title}>
                        <Locale stringKey="REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS__USER_TAGS_TITLE" />
                    </div>
                    <RequestReport<
                        ReviwsAnalysisUserTagTableDataType,
                        ReviewsAnalysisUserTagType,
                        ReviewsAnalysisTagsQueryType
                    >
                        apiHandler={getUserTags}
                        columns={userTagsPageTableColumns({
                            timeUnit: singleFilter.timeFormat || ReviewsAndAnswersStatisticsUnitMeasurementEnum.Minutes,
                        })}
                        defaultFilename={getLocalizedString('CATEGORY_NAME__TAGS')}
                        filters={{...singleFilter}}
                        formatDataForXls={formatDataForXls}
                        getDataSource={getDataSource}
                        title={<Locale stringKey="REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS__USER_TAGS_TITLE" />}
                        xlsxWorksheetName={dateRangeToWorksheetName({
                            rawStartDate: singleFilter.startDate,
                            rawEndDate: singleFilter.endDate,
                        })}
                    />
                </div>
            }
        >
            <ActiveFilter
                isCompareRangeAllowed
                isCompetitorAllowed
                isPeriodHidden
                isPreview={false}
                isTopicsAllowed={false}
                isWithTextOnly={false}
            />

            <CompareModeResolver
                renderCompetitor={null}
                renderPeriod={<UserTagsTablePeriod />}
                renderSingle={<UserTagsTableSingle />}
            />
        </PageCard>
    );
}
